import { useEffect, useRef, useState } from "react";
import { Modal, Typography, Button, Box, Alert, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import Input from "src/components/atoms/input";
import Select from "src/components/atoms/select";
import InputMaskCustom from "src/components/atoms/input-mask-custom";
import { FormContainer } from "src/pages/enrollment/styles";
import { FormGroup } from "src/pages/profile/editAthletes/styles";
import api from "src/services/api";
import { toast } from "react-toastify";
import {
  updateFederationAffiliation,
  createFederationPixPayment,
  getFederationAffiliationStatus,
  getFederationInfo,
} from "src/services/federationApi";

interface Fighter {
  id: number;
  name: string;
  mother_name: string;
  genre: string;
  phone: string;
  birth_date: string;
  gym: string;
  belt: string;
}

interface Props {
  fighter: Fighter;
  targetValue: number | null;
  onUpdated?: (updated: any) => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
  isFederated?: boolean;
  federationSlug?: string;
  federationName?: string;
  affiliationId?: string;
}

const arrBelts = [
  { title: "Branca", option: 1 },
  { title: "Cinza", option: 2 },
  { title: "Amarela", option: 3 },
  { title: "Laranja", option: 4 },
  { title: "Verde", option: 5 },
  { title: "Azul", option: 6 },
  { title: "Roxa", option: 7 },
  { title: "Marrom", option: 8 },
  { title: "Preta", option: 9 },
];

const beltNumberToName: Record<number, string> = {
  1: "Branca",
  2: "Cinza",
  3: "Amarela",
  4: "Laranja",
  5: "Verde",
  6: "Azul",
  7: "Roxa",
  8: "Marrom",
  9: "Preta",
};

type PixState = {
  pixQrCode: string;
  pixQrCodeImage: string;
  pixExpiresAt: string;
  priceCents: number;
  pricingTableId: string;
  affiliationId: string;
  pendingData: any;
  changedFields: string[];
};

export const EditFighterModal: React.FC<Props> = ({
  fighter,
  targetValue,
  onUpdated,
  trigger,
  open,
  onClose,
  isFederated,
  federationSlug,
  federationName,
  affiliationId,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pixState, setPixState] = useState<PixState | null>(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [pixExpired, setPixExpired] = useState(false);
  const [pixCountdown, setPixCountdown] = useState("");
  const [generatingPix, setGeneratingPix] = useState(false);
  const [dataChangePricing, setDataChangePricing] = useState<{
    priceCents: number;
    fieldKey: string;
  } | null>(null);
  const [federationAcademies, setFederationAcademies] = useState<
    Array<{ id: string; name: string; city: string | null; state: string | null }>
  >([]);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pixRegenerateCountRef = useRef(0);
  const MAX_PIX_REGENERATIONS = 3;
  const modalOpen = open ?? internalOpen;
  const handleOpen = () => (open === undefined ? setInternalOpen(true) : null);
  const handleClose = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPixState(null);
    setPaymentConfirmed(false);
    setPixExpired(false);
    setPixCountdown("");
    setGeneratingPix(false);
    pixRegenerateCountRef.current = 0;
    if (onClose) onClose();
    else setInternalOpen(false);
  };
  const token = localStorage.getItem("@torneiofacil/token");

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm();

  // Normalizar gênero para match com as options do select ("Masculino"/"Feminino")
  const normalizeGenre = (g: string | undefined): string => {
    if (!g) return "";
    const lower = g.trim().toLowerCase();
    if (lower === "masculino" || lower === "m") return "Masculino";
    if (lower === "feminino" || lower === "f") return "Feminino";
    return g;
  };

  useEffect(() => {
    if (modalOpen && fighter) {
      reset({
        mother_name: fighter.mother_name || "",
        genre: normalizeGenre(fighter.genre),
        phone: fighter.phone || "",
        birth_date: fighter.birth_date || "",
        gym: fighter.gym || "",
        belt: fighter.belt || "",
      });
      setPixState(null);
      setPaymentConfirmed(false);

      // Buscar regras de pricing da federação para mostrar custos
      if (isFederated && federationSlug) {
        getFederationInfo(federationSlug, token).then((info) => {
          const dcPricing = info.pricingTables.find(
            (p) => p.category === "DATA_CHANGE"
          );
          if (dcPricing) {
            setDataChangePricing({
              priceCents: dcPricing.priceCents,
              fieldKey: dcPricing.fieldKey || "",
            });
          }
          if (info.academies && info.academies.length > 0) {
            setFederationAcademies(info.academies);
          }
        }).catch(() => {});
      }
    }
  }, [modalOpen, fighter, reset]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Contagem regressiva do PIX
  useEffect(() => {
    if (!pixState?.pixExpiresAt || paymentConfirmed) return;
    const expires = new Date(pixState.pixExpiresAt).getTime();

    const tick = () => {
      const remaining = expires - Date.now();
      if (remaining <= 0) {
        setPixExpired(true);
        setPixCountdown("");
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000);
      setPixCountdown(
        `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
      );
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [pixState?.pixExpiresAt, paymentConfirmed]);

  const detectFederationChanges = (data: any) => {
    const changes: Record<string, unknown> = {};
    const changedFieldNames: string[] = [];

    if (data.phone && data.phone !== fighter.phone) {
      changes.phone = data.phone;
      changedFieldNames.push("phone");
    }
    if (data.birth_date && data.birth_date !== fighter.birth_date) {
      changes.birthDate = data.birth_date;
      changedFieldNames.push("birthDate");
    }
    if (data.gym && data.gym.trim().toLowerCase() !== fighter.gym?.trim()?.toLowerCase()) {
      // Se é federado e tem academias, enviar o academyId
      if (federationAcademies.length > 0) {
        const matched = federationAcademies.find(
          (a) => a.name.toLowerCase() === data.gym.trim().toLowerCase()
        );
        if (matched) {
          changes.academyId = matched.id;
        }
      }
      changedFieldNames.push("academy");
    }
    if (data.belt && String(data.belt) !== String(fighter.belt)) {
      const beltName = beltNumberToName[+data.belt] || String(data.belt);
      changes.customFields = { faixa: beltName };
      changedFieldNames.push("belt");
    }

    return { changes, changedFieldNames };
  };

  const startPixPolling = (affId: string, pendingData?: any) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    let errorCount = 0;
    pollingRef.current = setInterval(async () => {
      try {
        const result = await getFederationAffiliationStatus(
          federationSlug!,
          affId,
          token
        );
        errorCount = 0;
        const latestPayment = result.affiliation?.payments?.[0];
        const affiliationStatus = result.affiliation?.status;
        const isPaymentConfirmed = latestPayment?.status === "PAID" || latestPayment?.paidAt;
        const isAdminApproved = affiliationStatus === "ACTIVE";

        if (isPaymentConfirmed || isAdminApproved) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setPaymentConfirmed(true);
          toast.success(
            isPaymentConfirmed
              ? "Pagamento confirmado! Dados atualizados na federacao."
              : "Alteracao aprovada pelo administrador da federacao!"
          );

          // Aplicar dados localmente
          const dataToApply = pendingData || pixState?.pendingData;
          if (dataToApply) {
            if (isPaymentConfirmed) {
              // Re-chamar o update para que a federacao aplique as alteracoes (pagamento ja feito)
              try {
                const { changes } = detectFederationChanges(dataToApply);
                if (Object.keys(changes).length > 0) {
                  await updateFederationAffiliation(
                    federationSlug!,
                    affId,
                    changes,
                    token
                  );
                }
              } catch {
                // Federacao pode ja ter aplicado via webhook — continuar
              }
            }
            const dataToSave = { ...dataToApply };
            await syncFromFederation(dataToSave);
            await saveLocalFighter(dataToSave);
          }
        }
      } catch (err: any) {
        if (err?.response?.status === 401) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          toast.error("Sessao expirada. Faca login novamente.");
          return;
        }
        errorCount++;
        if (errorCount >= 5) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          toast.error("Erro ao verificar pagamento. Tente novamente mais tarde.");
        }
      }
    }, 5000);
  };

  const saveLocalFighter = async (data: any) => {
    const updatedFighter = {
      id: fighter.id,
      name: fighter.name.trim().toLowerCase(),
      mother_name: fighter.mother_name.trim().toLowerCase(),
      genre: data.genre.trim().toLowerCase(),
      phone: data.phone,
      birth_date: data.birth_date,
      gym: data.gym.trim().toLowerCase(),
      belt: +data.belt,
    };

    const endpoint = targetValue === 0 ? `/me` : `/fighters/${fighter.id}`;

    await api.put(
      endpoint,
      {
        birth_date: data.birth_date,
        gym: data.gym.trim().toLowerCase(),
        mother_name: fighter.mother_name.trim().toLowerCase(),
        name: fighter.name.trim().toLowerCase(),
        belt: +data.belt,
        phone: data.phone,
        genre: data.genre,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Atleta foi atualizado com sucesso!");
    onUpdated?.(updatedFighter);
    handleClose();
  };

  // Mapeamento de campos custom da federacao → campos do torneio
  // Usado para campos que existem no customFieldsJson da federacao
  // e tem equivalente no fighter do torneio
  const CUSTOM_FIELD_MAP: Record<string, { localKey: string; transform?: (v: unknown) => unknown }> = {
    genero: { localKey: "genre", transform: (v) => {
      const s = String(v).trim().toLowerCase();
      if (s === "masculino" || s === "m") return "Masculino";
      if (s === "feminino" || s === "f") return "Feminino";
      return v;
    }},
    sexo: { localKey: "genre", transform: (v) => {
      const s = String(v).trim().toLowerCase();
      if (s === "masculino" || s === "m") return "Masculino";
      if (s === "feminino" || s === "f") return "Feminino";
      return v;
    }},
    gender: { localKey: "genre", transform: (v) => {
      const s = String(v).trim().toLowerCase();
      if (s === "masculino" || s === "m" || s === "male") return "Masculino";
      if (s === "feminino" || s === "f" || s === "female") return "Feminino";
      return v;
    }},
  };

  // Buscar dados atualizados da federacao e sobrescrever campos compartilhados
  // Campos fixos do AfiliaJJi: name, cpf, phone, birthDate, academyId, status
  // Campos custom: faixa/belt, genero (se configurado pelo tenant)
  const syncFromFederation = async (data: any) => {
    if (!federationSlug || !affiliationId) return;
    try {
      const affStatus = await getFederationAffiliationStatus(
        federationSlug,
        affiliationId,
        token
      );
      const aff = affStatus.affiliation;
      if (!aff) return;

      const beltNameToNumber: Record<string, number> = {
        branca: 1, cinza: 2, amarela: 3, laranja: 4, verde: 5,
        azul: 6, roxa: 7, marrom: 8, preta: 9,
      };

      // Faixa (custom field extraido como campo tipado): federacao e fonte de verdade
      if (aff.belt) {
        const beltNum = beltNameToNumber[String(aff.belt).toLowerCase()];
        if (beltNum) data.belt = beltNum;
      }

      // Academia (campo fixo via FK): federacao e fonte de verdade
      if (aff.academy?.name) {
        data.gym = aff.academy.name.toLowerCase();
      }

      // Campos custom da federacao: mapear para campos do torneio
      // Ex: se a federacao tem "genero" no form builder, sincroniza com "genre" do fighter
      if (aff.customFields) {
        for (const [fedKey, fedValue] of Object.entries(aff.customFields)) {
          if (fedValue == null) continue;
          const mapping = CUSTOM_FIELD_MAP[fedKey] || CUSTOM_FIELD_MAP[fedKey.toLowerCase()];
          if (mapping) {
            data[mapping.localKey] = mapping.transform ? mapping.transform(fedValue) : fedValue;
          }
        }
      }
    } catch {
      // Se falhar, continuar com dados do formulario
    }
  };

  const onEdit = async (data: any) => {
    try {
      setSaving(true);

      if (isFederated && federationSlug && affiliationId) {
        const { changes, changedFieldNames } = detectFederationChanges(data);

        if (changedFieldNames.length > 0) {
          const result = await updateFederationAffiliation(
            federationSlug,
            affiliationId,
            changes,
            token
          );

          if (result.requiresPayment) {
            const pixResult = await createFederationPixPayment(
              federationSlug,
              affiliationId,
              result.pricingTableId!,
              token
            );

            setPixExpired(false);
            setPixState({
              pixQrCode: pixResult.pixQrCode,
              pixQrCodeImage: pixResult.pixQrCodeImage,
              pixExpiresAt: pixResult.pixExpiresAt,
              priceCents: result.priceCents!,
              pricingTableId: result.pricingTableId!,
              affiliationId: affiliationId,
              pendingData: data,
              changedFields: result.changedFields || changedFieldNames,
            });

            startPixPolling(affiliationId, data);
            setSaving(false);
            return;
          }

          if (result.requiresApproval) {
            // Alteração registrada, mas aguarda aprovação do admin da federação.
            // Não aplicar localmente — a federação é a fonte de verdade.
            toast.info(
              "Alteração enviada para aprovação do admin da federação. Você será notificado quando for aprovada."
            );
            setSaving(false);
            onClose?.();
            return;
          }
        }

        // Federacao atualizada sem pagamento — buscar dados atualizados
        // A federacao e a fonte de verdade para faixa e academia
        await syncFromFederation(data);
      } else {
        // Torneio federado mas sem mudancas na federacao — ainda assim sincronizar
        await syncFromFederation(data);
      }

      await saveLocalFighter(data);
    } catch (error) {
      console.error("Erro ao atualizar atleta:", error);
      toast.error("Ocorreu um erro ao atualizar o atleta.");
    } finally {
      setSaving(false);
    }
  };

  const fieldLabelMap: Record<string, string> = {
    name: "Nome",
    phone: "Telefone",
    birthDate: "Data de nascimento",
    birth_date: "Data de nascimento",
    academy: "Academia",
    academyId: "Academia",
    belt: "Faixa",
    email: "E-mail",
    photoUrl: "Foto",
    documentUrl: "Documento",
  };

  const handleRegeneratePix = async () => {
    if (!pixState || pixRegenerateCountRef.current >= MAX_PIX_REGENERATIONS) return;
    setGeneratingPix(true);
    try {
      const pixResult = await createFederationPixPayment(
        federationSlug!,
        pixState.affiliationId,
        pixState.pricingTableId,
        token
      );
      pixRegenerateCountRef.current += 1;
      setPixExpired(false);
      setPixState({
        ...pixState,
        pixQrCode: pixResult.pixQrCode,
        pixQrCodeImage: pixResult.pixQrCodeImage,
        pixExpiresAt: pixResult.pixExpiresAt,
      });
      startPixPolling(pixState.affiliationId, pixState.pendingData);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        toast.error("Sessao expirada. Faca login novamente.");
      } else {
        toast.error(err?.response?.data?.message || "Erro ao gerar novo QR Code.");
      }
    } finally {
      setGeneratingPix(false);
    }
  };

  const renderPixPayment = () => {
    if (!pixState) return null;

    if (paymentConfirmed) {
      return (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h6" sx={{ color: "green", mb: 1 }}>
            Pagamento confirmado!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seus dados foram atualizados na federacao.
          </Typography>
        </Box>
      );
    }

    const changedLabels = (pixState.changedFields || []).map(
      (f) => fieldLabelMap[f] || f
    );

    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="h6" gutterBottom>
          Pagamento necessario
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A federacao cobra{" "}
          <strong>
            R$ {(pixState.priceCents / 100).toFixed(2).replace(".", ",")}
          </strong>
          {" "}para alteracao de dados.
        </Typography>
        {changedLabels.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Campos alterados: <strong>{changedLabels.join(", ")}</strong>
          </Typography>
        )}
        {pixExpired ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
              O QR Code PIX expirou.
              {pixRegenerateCountRef.current < MAX_PIX_REGENERATIONS
                ? " Gere um novo para continuar."
                : " Limite de tentativas atingido. Entre em contato com a federacao."}
            </Alert>
            {pixRegenerateCountRef.current < MAX_PIX_REGENERATIONS && (
              <Button
                variant="contained"
                size="small"
                onClick={handleRegeneratePix}
                disabled={generatingPix}
                sx={{ textTransform: "none" }}
              >
                {generatingPix ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Gerar novo QR Code"
                )}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {pixState.pixQrCodeImage && (
              <Box sx={{ mb: 2 }}>
                <img
                  src={
                    pixState.pixQrCodeImage.startsWith("data:")
                      ? pixState.pixQrCodeImage
                      : pixState.pixQrCodeImage.startsWith("http")
                        ? pixState.pixQrCodeImage
                        : `data:image/png;base64,${pixState.pixQrCodeImage}`
                  }
                  alt="QR Code PIX"
                  style={{ maxWidth: 220, margin: "0 auto" }}
                />
              </Box>
            )}
            {pixState.pixQrCode && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(pixState.pixQrCode);
                    toast.info("Codigo PIX copiado!");
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Copiar codigo PIX
                </Button>
              </Box>
            )}
            {pixCountdown && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Expira em{" "}
                <strong style={{ fontFamily: "monospace", fontSize: 15 }}>
                  {pixCountdown}
                </strong>
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Aguardando pagamento...
              </Typography>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <div onClick={handleOpen}>{trigger}</div>
      <Modal
        open={modalOpen}
        onClose={handleClose}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <div
          className="modal-wrapper"
          style={{
            width: "100%",
            maxWidth: "600px",
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          {pixState ? (
            <>
              <Typography variant="h5" gutterBottom>
                Editar Atleta
              </Typography>
              {renderPixPayment()}
              {paymentConfirmed && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleClose}
                  sx={{ width: "100%", color: "white", backgroundColor: "#373737", mt: 2 }}
                >
                  Fechar
                </Button>
              )}
            </>
          ) : (
            <FormContainer onSubmit={handleSubmit(onEdit)}>
              <Typography variant="h5" gutterBottom>
                Editar Atleta
              </Typography>
              {isFederated && federationName && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Alguns campos são controlados pela federação{" "}
                  <strong>{federationName}</strong>.
                  {dataChangePricing ? (
                    dataChangePricing.fieldKey === "*" ? (
                      <>
                        {" "}Qualquer alteração de dados tem custo de{" "}
                        <strong>
                          R$ {(dataChangePricing.priceCents / 100).toFixed(2).replace(".", ",")}
                        </strong>.
                      </>
                    ) : (
                      <>
                        {" "}Alterar{" "}
                        <strong>{dataChangePricing.fieldKey || "campos federados"}</strong>
                        {" "}tem custo de{" "}
                        <strong>
                          R$ {(dataChangePricing.priceCents / 100).toFixed(2).replace(".", ",")}
                        </strong>.
                        {" "}Demais campos são gratuitos.
                      </>
                    )
                  ) : (
                    <> Alterações em campos federados podem ter custo adicional.</>
                  )}
                </Alert>
              )}
              {(() => {
                const isWildcard = dataChangePricing?.fieldKey === "*";
                const isAcademyPriced = isFederated && dataChangePricing &&
                  (isWildcard || dataChangePricing.fieldKey?.toLowerCase().includes("academia"));
                const priceLabel = dataChangePricing
                  ? `R$ ${(dataChangePricing.priceCents / 100).toFixed(2).replace(".", ",")}`
                  : "";
                const academyLabel = isAcademyPriced ? `Academia (${priceLabel} para alterar)` : "Academia";
                return (
                  <>
                    <FormGroup>
                      {isFederated && federationAcademies.length > 0 ? (
                        <Select
                          label={academyLabel}
                          name="gym"
                          register={register}
                          required
                          hasError={!!errors.gym}
                          error="Este campo é obrigatório"
                          options={federationAcademies.map((a) => ({
                            title: a.city ? `${a.name} - ${a.city}${a.state ? `/${a.state}` : ""}` : a.name,
                            option: a.name.toLowerCase(),
                          }))}
                        />
                      ) : (
                        <Input
                          label={academyLabel}
                          name="gym"
                          type="text"
                          register={register}
                          required
                          hasError={!!errors.gym}
                          error="Este campo é obrigatório"
                        />
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Select
                        label={isWildcard && isFederated ? `Faixa (${priceLabel} para alterar)` : "Faixa"}
                        name="belt"
                        register={register}
                        required
                        hasError={!!errors.belt}
                        error="Este campo é obrigatório"
                        options={arrBelts}
                      />
                    </FormGroup>
                  </>
                );
              })()}
              <FormGroup>
                <Select
                  label="Sexo"
                  name="genre"
                  register={register}
                  required
                  hasError={!!errors.genre}
                  error="Este campo é obrigatório"
                  options={[
                    { title: "Masculino", option: "Masculino" },
                    { title: "Feminino", option: "Feminino" },
                  ]}
                />
              </FormGroup>
              <FormGroup>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <InputMaskCustom
                      {...field}
                      label="Telefone"
                      mask="(99) 99999-9999"
                      required
                      hasError={!!errors.phone}
                      error="Este campo é obrigatório"
                    />
                  )}
                />
              </FormGroup>
              <FormGroup>
                <Controller
                  name="birth_date"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <InputMaskCustom
                      {...field}
                      label="Data de nascimento"
                      mask="99/99/9999"
                      required
                      hasError={!!errors.birth_date}
                      error="Este campo é obrigatório"
                    />
                  )}
                />
              </FormGroup>
              <Button
                variant="outlined"
                type="submit"
                size="large"
                disabled={saving}
                sx={{ width: "100%", color: "white", backgroundColor: "#373737" }}
              >
                {saving ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Atualizar atleta"
                )}
              </Button>
            </FormContainer>
          )}
        </div>
      </Modal>
    </>
  );
};

export default EditFighterModal;
